import {Injectable} from '@angular/core';
import {NavigationEnd, Router, ActivatedRouteSnapshot} from '@angular/router';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, startWith} from 'rxjs/operators';

/**
 * Service to access the current projectId from the route. This is useful for components that are
 * used in different routes and need to access the projectId without having to pass it
 * down through multiple levels of components.
 */
@Injectable({providedIn: 'root'})
export class CurrentProjectService {
    constructor(private readonly router: Router) {
    }

    getCurrentProjectId(): ProjectId | undefined {
        return this.findProjectId(this.router.routerState.snapshot.root);
    }

    subscribeToCurrentProjectId(): Observable<ProjectId | undefined> {
        return this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.getCurrentProjectId()),
            startWith(this.getCurrentProjectId()),
            distinctUntilChanged()
        );
    }

    private findProjectId(route: ActivatedRouteSnapshot | null): ProjectId | undefined {
        if (!route) {
            return undefined;
        }

        if (route.params?.projectId) {
            return route.params.projectId;
        }

        for (const child of route.children || []) {
            const childProjectId = this.findProjectId(child);
            if (childProjectId) {
                return childProjectId;
            }
        }

        return undefined;
    }
}
